/**
 * Procurement deal value resolution.
 * BE-precomputed USD only for totals — never treat native deal_value as USD unless currency is USD.
 * FX conversion happens once at the BE (deal_value_usd via toUSD).
 */

function positiveNumber(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return !Number.isNaN(n) && n > 0 ? n : null;
}

function isUsdCurrency(deal) {
  const c = String(deal?.currency || '').trim().toUpperCase();
  return c === '' || c === 'USD' || c === 'US' || c === 'US$' || c === 'USDB';
}

/** Numeric value for SORTING and single-deal display — excludes non-USD native amounts without deal_value_usd. */
export function getDealDisplayValueUSD(deal) {
  return resolveDealDisplayValue(deal) ?? 0;
}

/**
 * A member campus's SHARE of a system-wide contract (2026-10-11 system-deal model).
 * The system's own deal carries the contract value; each member campus carries no deal_value and
 * its share in neo_estimated_value. The share is real for that campus's own view, but summing it
 * next to the system deal counts the same money twice.
 */
export const SYSTEM_SHARE_COHORTS = new Set([
  'systemwide_value_allocated_by_member_enrolment',
  'member_value_from_published_per_student_rate',
]);

export function isSystemShareDeal(deal) {
  return !!deal && !positiveNumber(deal.deal_value_usd) && SYSTEM_SHARE_COHORTS.has(String(deal.neo_cohort_level || ''));
}

/** Numeric value for TOTALS across deals — a member's system share is excluded (the system deal already counts it). */
export function getDealValueUSDForTotals(deal) {
  if (isSystemShareDeal(deal)) return 0;
  return resolveDealDisplayValue(deal) ?? 0;
}

/**
 * Authoritative USD for totals: deal_value_usd → neo_estimated_value.
 * Native fallback only when currency is USD (or absent).
 */
export function resolveDealDisplayValue(deal) {
  if (!deal) return null;
  const usd = positiveNumber(deal.deal_value_usd) ?? positiveNumber(deal.neo_estimated_value);
  if (usd != null) return usd;
  if (isUsdCurrency(deal)) {
    return (
      positiveNumber(deal.deal_value) ??
      positiveNumber(deal.contract_value) ??
      positiveNumber(deal.value)
    );
  }
  return null;
}

/** @returns {'usd_actual' | 'neo' | 'legacy' | 'native_no_fx' | 'none'} */
export function getDealValueSource(deal) {
  if (!deal) return 'none';
  if (positiveNumber(deal.deal_value_usd) != null) return 'usd_actual';
  if (positiveNumber(deal.neo_estimated_value) != null) return 'neo';
  const hasNative =
    positiveNumber(deal.deal_value) != null ||
    positiveNumber(deal.contract_value) != null ||
    positiveNumber(deal.value) != null;
  if (!hasNative) return 'none';
  if (isUsdCurrency(deal)) return 'legacy';
  return 'native_no_fx';
}

export function hasActualDealValueUSD(deal) {
  return getDealValueSource(deal) === 'usd_actual';
}

export function hasNeoEstimatedValueUSD(deal) {
  return getDealValueSource(deal) === 'neo';
}

export function getLegacyDealValue(deal) {
  if (!deal) return null;
  return (
    positiveNumber(deal.deal_value) ??
    positiveNumber(deal.contract_value) ??
    positiveNumber(deal.value)
  );
}
